// StepLeadCapture.jsx

import { motion } from "framer-motion";
import { useState, useRef } from "react";
import { fadeUp, stagger, micro } from "../../../../utils/motion";
import { Mail, User, Phone } from "lucide-react";
import { Link } from "react-router-dom";

export default function StepLeadCapture({ payload = {}, onSubmit }) {

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [accepted, setAccepted] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  const honeypotRef = useRef("");
  const startTimeRef = useRef(Date.now());

  function updateField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function validEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async function submit() {

    setError("");

    if (honeypotRef.current) return;

    const seconds = (Date.now() - startTimeRef.current) / 1000;
    if (seconds < 2) return;

    if (!form.name || !form.email) {
      setError("Enter your name and email");
      return;
    }

    if (!validEmail(form.email)) {
      setError("Enter a valid email address");
      return;
    }

    if (!accepted) {
      setError("Please accept Terms & Conditions");
      return;
    }

    setWorking(true);

    try {
      await onSubmit?.({
        name: form.name,
        email: form.email,
        phone: form.phone || null,

        // 🔥 CONSENT
        consent: {
          termsAccepted: true,
          termsVersion: "15-04-2026",
          timestamp: Date.now(),
        },
      });

    } catch (e) {
      setError("Something went wrong. Try again.");
    } finally {
      setWorking(false);
    }
  }

  const isValid = form.name && validEmail(form.email) && accepted;

  return (
    <motion.div
      variants={stagger()}
      initial="hidden"
      animate="show"
      className="max-w-content space-y-8"
    >

      {/* HEADER */}
      <motion.div variants={fadeUp} className="space-y-2">
        <h2 className="text-title font-extrabold text-text-primary">
          Get your performance report
        </h2>

        <p className="text-body text-text-muted max-w-reading">
          We’ll send tailored insights and revenue strategy for your property.
        </p>
      </motion.div>

      {/* HONEYPOT */}
      <input
        type="text"
        style={{ display: "none" }}
        onChange={(e) => (honeypotRef.current = e.target.value)}
      />

      {/* FORM CARD */}
      <motion.div
        variants={fadeUp}
        className="
          rounded-[24px]
          border border-border-soft
          bg-bg-surface
          p-5 md:p-6
          space-y-4
        "
      >

        <InputField
          icon={User}
          placeholder="Full name"
          value={form.name}
          onChange={(v) => updateField("name", v)}
        />

        <InputField
          icon={Mail}
          placeholder="Email address"
          value={form.email}
          onChange={(v) => updateField("email", v)}
        />

        <InputField
          icon={Phone}
          placeholder="Phone number"
          value={form.phone}
          onChange={(v) => updateField("phone", v)}
        />

        {/* 🔥 CONSENT */}
        <div className="flex items-start gap-2 pt-1">

          <input
            type="checkbox"
            checked={accepted}
            onChange={() => setAccepted(!accepted)}
            className="mt-1 accent-brand-primary"
          />

          <p className="text-small text-text-muted leading-snug">
            I accept the{" "}
            <Link
              to="/terms"
              target="_blank"
              className="text-brand-primary underline"
            >
              Terms & Conditions
            </Link>
          </p>

        </div>

        {/* ERROR */}
        {error && (
          <div className="text-small text-red-400 pt-1">
            {error}
          </div>
        )}

      </motion.div>

      {/* CTA */}
      <motion.button
        variants={fadeUp}
        whileHover={isValid ? micro.hover : {}}
        whileTap={isValid ? micro.tap : {}}
        onClick={submit}
        disabled={working || !isValid}
        className="
          w-full py-3 rounded-xl
          bg-brand-primary text-white font-semibold
          disabled:opacity-40 disabled:cursor-not-allowed
        "
      >
        {working ? "Sending..." : "Get my report"}
      </motion.button>

      {/* FOOTNOTE */}
      <motion.div
        variants={fadeUp}
        className="text-micro text-text-muted text-center"
      >
        No spam • Instant insights • Secure submission
      </motion.div>

    </motion.div>
  );
}


/* INPUT FIELD */

function InputField({ icon: Icon, placeholder, value, onChange }) {
  return (
    <div
      className="
        flex items-center gap-3
        rounded-xl border border-border-soft
        bg-bg-light px-4 py-3
        transition
        focus-within:border-brand-primary
      "
    >

      <Icon size={16} className="text-text-muted" />

      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full bg-transparent outline-none
          text-text-primary
          placeholder:text-text-muted/60
        "
      />

    </div>
  );
}